import { Address } from "./embeddable/address";
import { OrganizationEntityEntity } from "./organizationEntity";
import { TaxLocation } from "./taxLocation";
import { User, UserBase } from "./user";

export enum EntityIndustry {
    CREATOR = "CREATOR",
    REAL_ESTATE = "REAL_ESTATE",
    OTHER = "OTHER",
}

export enum EntityCloseStatus {
    TODO = "todo",
    IN_PROGRESS = "in_progress",
    DONE = "done",
}

export enum EntityCloseStage {
    NOT_STARTED = "not_started",
    WAITING_ON_CLIENT = "waiting_on_client",
    WAITING_ON_ORGANIZATION = "waiting_on_organization",
    DELIVERED = "delivered",
}

export enum TransactionEnrichmentPauseReason {
    MIGRATED_JE_RULE_SEEDING = "migrated_je_rule_seeding",
    CATEGORY_COA_MAPPING = "category_coa_mapping",
}

/**
 * Base type for Entity - allows undefined on relations.
 * Used by entity classes (TypeORM).
 */
export interface EntityBase {
    id: number;
    uuid: string;
    name: string | null;
    address: Address | null;
    bookkeepingYears: number[];
    bookkeepingStartDate: string;
    taxLocations: TaxLocation[];
    onboardingComplete: boolean;
    industry: EntityIndustry;
    iconColor: string;
    logo: string | null;
    isBusiness: boolean;
    isPersonal: boolean;
    user?: UserBase | null;
    userId: number | null;
    profile: EntityProfile | null;
    legalType: EntityProfileType | null;
    isMockEntity: boolean;
    /**
     * Internal billing flag; may be absent in client-facing shapes.
     */
    excludeFromMetering?: boolean;
    workspaceId: string;
    openingBalanceSubmitted: boolean;
    reportsReady: boolean;
    transactionEnrichmentPausedAt: Date | null;
    federalTaxRate: number;
    allowAITrainingOnTheirData: boolean;
    hasCustomChartOfAccounts: boolean;
    organizationAssignments?: OrganizationEntityEntity[];
}

/**
 * Client-facing type
 * Used by DTOs and service boundaries.
 */
export type Entity = Omit<EntityBase, "excludeFromMetering">;

/**
 * Extended Entity type - user relation guaranteed loaded.
 * Used when entity.user is required (e.g. personal entity display).
 */
export interface EntityWithUser extends Omit<Entity, "user"> {
    user: User | null;
}

// will be renamed to EntityType after old onboarding is removed
export enum EntityProfileType {
    SOLE_PROPRIETORSHIP = "sole_proprietorship",
    SMLLC = "smllc",
    MMLLC = "mmllc",
    S_CORP = "s_corp",
    PARTNERSHIP = "partnership",
    C_CORP = "c_corp",
    NO_LEGAL_ENTITY = "no_legal_entity",
}

export enum EntityProfileIndustry {
    ACCOUNTANT = "accountant",
    AGENCY = "agency",
    BOOKKEEPER = "bookkeeper",
    CONSTRUCTION = "construction",
    CONSULTING = "consulting",
    CONTENT = "content",
    CRYPTO = "crypto",
    TRUCKING = "trucking",
    FINANCIAL_ADVISOR = "financial_advisor",
    INVESTOR = "investor",
    LEGAL = "legal",
    MANUFACTURING = "manufacturing",
    MEDICAL = "medical",
    NON_PROFIT = "non_profit",
    PHYSICAL_GOODS = "physical_goods",
    PROPERTY_MANAGEMENT = "property_management",
    PSYCHOLOGIST = "psychologist",
    REAL_ESTATE_AGENT = "real_estate_agent",
    REAL_ESTATE_INVESTOR = "real_estate_investor",
    RESTAURANT = "restaurant",
    RETAIL = "retail",
    SAAS = "saas",
    TRADEPERSON = "tradeperson",
    ENTERTAINMENT = "entertainment",
    OTHER = "other",
}

export enum ProfessionalType {
    CORPORATE_ACCOUNTANT = "corporate_accountant",
    FIRM_ACCOUNTANT = "firm_accountant",
    BOOKKEEPER = "bookkeeper",
    TAX_ADVISOR = "tax_advisor",
}

export enum FirmSize {
    "1_19" = "1_19",
    "20_49" = "20_49",
    "50_99" = "50_99",
    "100_199" = "100_199",
    "200_499" = "200_499",
    "500_PLUS" = "500_PLUS",
}

// Business tax returns a firm files per year.
export enum BusinessTaxReturns {
    "0_249" = "0_249",
    "250_499" = "250_499",
    "500_999" = "500_999",
    "1000_2499" = "1000_2499",
    "2500_PLUS" = "2500_PLUS",
}

export enum EntityProfileRevenueBracket {
    LESS_THAN_50K = "<$50k",
    BETWEEN_50K_AND_200K = "$50k - $200k",
    BETWEEN_200K_AND_1M = "$200k - $1M",
    MORE_THAN_1M = "$1M+",
}

export interface EntityProfile {
    description?: string;
    isPartnership?: boolean;
    partnershipPercentage?: number;
    website?: string;
    annualExpenses?: number;
    // entityType?: EntityType;

    // Onboarding V2 profile
    annualRevenue?: EntityProfileRevenueBracket;
    // type?: EntityProfileType;
    industry?: EntityProfileIndustry;
    industries?: EntityProfileIndustry[];

    // Onboarding V2 firm info
    professionalType?: ProfessionalType;
    firmSize?: FirmSize;
    howDidYouLearn?: string;

    // Onboarding V2 waitlist
    waitlistComment?: string;

    payrollProviders?: string[];
    paymentProcessorRequest?: string;

    // Onboarding V3
    country?: string;
    source?: string;
    ignoreWaitlist?: boolean;
    industryOther?: string;
}

/**
 * System-level metadata stored on an entity, used for tracking internal
 * automation state that should not be exposed to users.
 */
export interface EntityMetadata {
    /** Set once the low-value uncategorized-transactions task has been created. */
    uncategorizedTaskCreatedAt?: string;
    /** Set once the material (high-value) uncategorized-transactions task has been created. */
    materialTaskCreatedAt?: string;
    /** Set once the P&L activation task has been created. */
    profitLossTaskCreatedAt?: string;
}
